import { brandColor } from '../styles/brand-colors';
import { Ellipse, Path, Rect, Svg } from '../svg';
import { useTheme } from '../theme';

/**
 * Neutral greys for the artwork, per color scheme — read from the DS palette
 * (`brandColor`), never typed as hex. Raw colours rather than palette
 * classNames because SVG fills are *props*, not classes (the exception
 * `onboardingFeaturePalette.ts` documents).
 *
 * Dark is NOT the plain mirror. Mirrored, the "?" bubble became the darkest
 * thing in the picture — darker than the panel it sits on, a hole — and the
 * chips lighter than the bubble in front of them. Dark keeps light's ORDER
 * instead (bubble brightest, chips over the shadow, all above the surface),
 * the same rule as the web empty-folder art.
 */
const RAMP = {
  light: {
    /** secondary-100 — the tilted "?" bubble card. */
    bubble: brandColor('secondary', '100', false),
    /** secondary-200 — ground shadow + light answer chip. */
    soft: brandColor('secondary', '200', false),
    /** secondary-300 — question bar + answer chips. */
    strong: brandColor('secondary', '300', false),
    /** secondary-400 — the "?" glyph. */
    glyph: brandColor('secondary', '400', false),
  },
  dark: {
    bubble: brandColor('secondary', '500', true),
    soft: brandColor('secondary', '200', true),
    strong: brandColor('secondary', '400', true),
    glyph: brandColor('secondary', '300', true),
  },
};

/**
 * "No quizzes yet" — grey skeleton quiz cards with a "?" bubble. Drawn once
 * against the shared SVG primitives so web (deck-shell quiz empty states)
 * and native (quizzes dashboard empty state) render the SAME artwork —
 * previously duplicated in flashka-web assets and a flashka-app SvgXml copy.
 * Theme-aware via the shared `useTheme()` (design review: the light greys
 * glowed on dark surfaces).
 */
export function NoQuizzesIllustration({
  width = 200,
  height = 160,
}: {
  width?: number;
  height?: number;
}) {
  const { isDark } = useTheme();
  const ramp = isDark ? RAMP.dark : RAMP.light;
  return (
    <Svg width={width} height={height} viewBox="0 0 200 160" fill="none">
      <Ellipse cx="100.292" cy="133.369" rx="82.1542" ry="19.5884" fill={ramp.soft} />
      <Rect x="33" y="45" width="134" height="33" rx="10" fill={ramp.strong} />
      <Rect x="33" y="82" width="65" height="18" rx="6" fill={ramp.soft} />
      <Rect x="33" y="104" width="65" height="18" rx="6" fill={ramp.strong} />
      <Rect x="102" y="82" width="65" height="18" rx="6" fill={ramp.strong} />
      <Rect x="102" y="104" width="65" height="18" rx="6" fill={ramp.strong} />
      <Rect
        x="134.167"
        y="14.6758"
        width="51.7268"
        height="51.7268"
        rx="13.7938"
        transform="rotate(10.3667 134.167 14.6758)"
        fill={ramp.bubble}
      />
      <Path
        d="M151.932 48.3326L152.142 47.8028C153.624 44.066 158.522 44.3303 159.716 41.3186C160.369 39.6733 159.624 38.3774 158.007 37.7359C156.166 37.0059 154.57 37.6957 153.917 39.341L149.929 37.7593C151.383 34.0115 155.223 32.6299 159.574 34.3554C164.063 36.1362 165.409 39.252 164.093 42.5705C162.334 47.0044 156.863 46.6415 155.724 49.5138L155.613 49.7927L151.932 48.3326ZM149.459 54.1613L150.952 50.3967L154.912 51.9673L153.418 55.732L149.459 54.1613Z"
        fill={ramp.glyph}
      />
    </Svg>
  );
}
